import useSWRImmutable from 'swr/immutable'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from './bigNumber'
import {
  PRICE_REFRESH_MS,
  PriceToken,
  TokenPrice,
  TokenPriceState,
  fetchTokenPrice,
  derivePriceStatus,
} from './tokenPrice'

/**
 * USD prices of the CADINU ecosystem tokens and BNB.
 *
 * Source: the app's own `/api/price/<token>` (apps/web/src/pages/api/price/[token].ts). CADINU and CBON are the mid price of their
 * deepest verified pool read from the chain, BNB is PancakeSwap V2 WBNB/USDT reserves: the same numbers the token pages show (site
 * review 59 #3). CAKE still falls back to the farms.cadinu.io service.
 *
 * States are explicit (addendum §5): `loading` (no price yet), `ready`, `stale` (last fetch
 * failed or the upstream timestamp is old, previous price kept), `unavailable` (never got a
 * price). Nothing here ever turns "unknown" into 0 — the legacy `*AsBN` hooks still return
 * BIG_ZERO for arithmetic callers, and views must use `useTokenPriceState` to decide what to show.
 */
export type { PriceToken, PriceStatus, TokenPrice, TokenPriceState } from './tokenPrice'
export { PRICE_API_ORIGIN, PRICE_STALE_AFTER_MS, PRICE_REFRESH_MS, parsePriceResponse, priceUrlsFor, fetchTokenPrice, derivePriceStatus } from './tokenPrice'

const comparePrices = (a?: TokenPrice, b?: TokenPrice) => {
  if (!a && !b) return true
  if (!a || !b) return false
  return a.price.eq(b.price) && a.updatedAt === b.updatedAt
}

export const useTokenPriceQuery = (token: PriceToken) =>
  useSWRImmutable<TokenPrice>([`token-usd-price`, token], () => fetchTokenPrice(token), {
    refreshInterval: PRICE_REFRESH_MS,
    dedupingInterval: 5_000,
    // bounded automatic retry; the UI offers a manual retry through TokenPriceState.retry
    errorRetryCount: 3,
    errorRetryInterval: 5_000,
    keepPreviousData: true,
    compare: comparePrices,
  })

export const useTokenPriceState = (token: PriceToken): TokenPriceState => {
  const { data, error, mutate } = useTokenPriceQuery(token)
  // no react import here on purpose: this package has react only as a peer and its tests run without it
  return {
    price: data?.price,
    status: derivePriceStatus(data, error),
    updatedAt: data?.updatedAt,
    error: error ? String((error as Error)?.message ?? error) : undefined,
    retry: () => {
      mutate()
    },
  }
}

export const useCadinuPriceState = () => useTokenPriceState('cadinu')
export const useCbonPriceState = () => useTokenPriceState('cbon')
export const useCakePriceState = () => useTokenPriceState('cake')
export const useBnbPriceState = () => useTokenPriceState('bnb')

// ---- legacy shapes kept for existing callers -------------------------------------------------

/** @deprecated use useTokenPriceState('cake'); `data` is the price as a string, undefined until known */
export const useCakePrice = () => useLegacyPrice('cake')
/** @deprecated use useTokenPriceState('cbon') */
export const useCbonPrice = () => useLegacyPrice('cbon')
/** @deprecated use useTokenPriceState('cadinu') */
export const useCadinuPrice = () => useLegacyPrice('cadinu')

const useLegacyPrice = (token: PriceToken) => {
  const { data, error, isLoading, mutate } = useTokenPriceQuery(token)
  return { data: data?.price.toString(), error, isLoading, mutate }
}

/**
 * Arithmetic helpers. They return BIG_ZERO while the price is unknown so that existing maths
 * does not crash; anything user-visible must check `useTokenPriceState().status` first.
 */
const usePriceAsBN = (token: PriceToken): BigNumber => {
  const { data } = useTokenPriceQuery(token)
  return data?.price ?? BIG_ZERO
}
export const useCakePriceAsBN = () => usePriceAsBN('cake')
export const useCadinuPriceAsBN = () => usePriceAsBN('cadinu')
export const useCbonPriceAsBN = () => usePriceAsBN('cbon')
export const useBnbPriceAsBN = () => usePriceAsBN('bnb')
